import {
  actualSeconds,
  type CastMember,
  type CharacterDto,
  type DialogueLine,
  LANGUAGES,
  MAX_REFERENCE_PICTURES,
  type PictureRef,
  type ProjectDto,
  SHOT_MAX_SECONDS,
  SHOT_MIN_SECONDS,
  type ShotData,
} from '@studio/shared';
import { ArrowDown, ArrowUp, ImagePlus, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NativeSelect, SwitchRow } from '@/components/ui/controls';
import { Input, Label, Textarea } from '@/components/ui/form';
import { errorMessage, uploadImage } from '@/lib/api';
import { newId } from '@/lib/utils';
import { SectionTitle } from './common';

interface Props {
  data: ShotData;
  onChange: (d: ShotData) => void;
  project: ProjectDto;
  shotId: string;
  characters: CharacterDto[];
  /** Mode the prompt is built in. */
  mode: 'text' | 'references';
}

function L({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <Label className="mb-1 block">
      {children}
      {hint && <span className="ml-1 font-normal text-muted-foreground">{hint}</span>}
    </Label>
  );
}

/** Small preview of a reference picture. */
function pictureUrl(ref: PictureRef, project: ProjectDto, characters: CharacterDto[]): string | null {
  if (ref.type === 'character') {
    const c = characters.find((x) => x.id === ref.characterId);
    return c?.images[ref.imageIndex]?.url ?? c?.images[0]?.url ?? null;
  }
  if (ref.type === 'product') return project.product?.referenceImage?.url ?? null;
  if (ref.type === 'upload') return `/api/media/${ref.mediaId}`;
  const shot = project.shots.find((s) => s.id === ref.shotId);
  const take = shot?.takes.find((t) => t.id === shot.chosenTakeId);
  return (ref.at === 'last' ? take?.lastFrame?.url : take?.thumbnail?.url) ?? null;
}

function pictureLabel(ref: PictureRef, project: ProjectDto, characters: CharacterDto[]): string {
  if (ref.type === 'character')
    return `${characters.find((x) => x.id === ref.characterId)?.name ?? 'Character'} (picture ${ref.imageIndex + 1})`;
  if (ref.type === 'product') return project.product?.title ?? 'Product';
  if (ref.type === 'upload') return 'Uploaded picture';
  const n = project.shots.findIndex((s) => s.id === ref.shotId) + 1;
  return `Shot ${n || '?'} — ${ref.at === 'last' ? 'last frame' : ref.at === 'mid' ? 'middle' : `at ${ref.at} s`}`;
}

export function ShotEditor({ data, onChange, project, shotId, characters, mode }: Props) {
  const set = <K extends keyof ShotData>(k: K, v: ShotData[K]) => onChange({ ...data, [k]: v });
  const index = project.shots.findIndex((s) => s.id === shotId);
  const otherShots = project.shots
    .map((s, i) => ({ s, n: i + 1 }))
    .filter((x) => x.s.kind === 'shot' && x.s.id !== shotId);
  const castChars = project.cast;
  const knownChars = [...castChars, ...characters.filter((c) => !castChars.some((x) => x.id === c.id))];
  const charName = (id: string) => knownChars.find((c) => c.id === id)?.name ?? 'Unknown';
  const [addKind, setAddKind] = useState('');

  // ── who is in the shot ─────────────────────────────────────────────
  const inShot = (type: CastMember['type'], id: string) => data.cast.some((m) => m.type === type && m.id === id);
  const toggleCast = (type: CastMember['type'], id: string) =>
    set(
      'cast',
      inShot(type, id)
        ? data.cast.filter((m) => !(m.type === type && m.id === id))
        : [...data.cast, { type, id, note: '', picture: null }],
    );
  const updateMember = (i: number, patch: Partial<CastMember>) =>
    set(
      'cast',
      data.cast.map((m, j) => (j === i ? { ...m, ...patch } : m)),
    );
  const moveMember = (i: number, d: -1 | 1) => {
    const next = [...data.cast];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    set('cast', next);
  };

  // ── pictures ───────────────────────────────────────────────────────
  const addPicture = (ref: PictureRef) => {
    if (data.pictures.length >= MAX_REFERENCE_PICTURES) return;
    const pictures = [...data.pictures, ref];
    let cast = data.cast;
    // Point the matching subject at the new picture.
    const n = pictures.length;
    if (ref.type === 'character') {
      if (!cast.some((m) => m.type === 'character' && m.id === ref.characterId))
        cast = [...cast, { type: 'character', id: ref.characterId, note: '', picture: n }];
      else
        cast = cast.map((m) =>
          m.type === 'character' && m.id === ref.characterId && !m.picture ? { ...m, picture: n } : m,
        );
    }
    if (ref.type === 'product') {
      if (!cast.some((m) => m.type === 'product' && m.id === ref.productId))
        cast = [{ type: 'product', id: ref.productId, note: '', picture: n }, ...cast];
      else
        cast = cast.map((m) =>
          m.type === 'product' && m.id === ref.productId && !m.picture ? { ...m, picture: n } : m,
        );
    }
    onChange({ ...data, pictures, cast });
  };
  const removePicture = (i: number) => {
    const n = i + 1;
    onChange({
      ...data,
      pictures: data.pictures.filter((_, j) => j !== i),
      cast: data.cast.map((m) =>
        m.picture === n ? { ...m, picture: null } : m.picture && m.picture > n ? { ...m, picture: m.picture - 1 } : m,
      ),
    });
  };
  const onAddKind = async (value: string) => {
    setAddKind('');
    if (!value) return;
    const [kind, a, b] = value.split('|');
    if (kind === 'character') addPicture({ type: 'character', characterId: a!, imageIndex: Number(b) });
    if (kind === 'product') addPicture({ type: 'product', productId: a! });
    if (kind === 'shot') addPicture({ type: 'shotFrame', shotId: a!, at: b === 'last' ? 'last' : 'mid' });
    if (kind === 'upload') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/png,image/jpeg,image/webp';
      input.onchange = async () => {
        const f = input.files?.[0];
        if (!f) return;
        try {
          const m = await uploadImage(f);
          addPicture({ type: 'upload', mediaId: m.id });
        } catch (err) {
          toast.error(errorMessage(err));
        }
      };
      input.click();
    }
  };

  // ── lines ──────────────────────────────────────────────────────────
  const setLine = (i: number, patch: Partial<DialogueLine>) =>
    set(
      'lines',
      data.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)),
    );
  const addLine = () => {
    const speaker = data.cast.find((m) => m.type === 'character')?.id ?? castChars[0]?.id ?? null;
    set('lines', [
      ...data.lines,
      {
        id: newId(),
        characterId: speaker,
        speakerName: speaker ? '' : 'A narrator',
        delivery: 'says',
        voice: '',
        language: '',
        text: '',
        cut: 0,
      },
    ]);
  };
  const moveLine = (i: number, d: -1 | 1) => {
    const next = [...data.lines];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    set('lines', next);
  };

  const insertToken = (token: string) =>
    set('picture', `${data.picture}${data.picture && !data.picture.endsWith(' ') ? ' ' : ''}${token}`);
  const startOptions = otherShots.filter((x) => x.n < index + 1);
  const startsFrom = data.startFrom;

  return (
    <div className="space-y-1">
      <SectionTitle>Basics</SectionTitle>
      <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
        <div>
          <L>Name of the shot</L>
          <Input value={data.label} onChange={(e) => set('label', e.target.value)} placeholder="e.g. Stomach factory" />
        </div>
        <div>
          <L hint={`≈ ${actualSeconds(data.seconds).toFixed(2)} s`}>Seconds</L>
          <Input
            type="number"
            min={SHOT_MIN_SECONDS}
            max={SHOT_MAX_SECONDS}
            step={0.5}
            value={data.seconds}
            onChange={(e) =>
              set(
                'seconds',
                Math.min(SHOT_MAX_SECONDS, Math.max(SHOT_MIN_SECONDS, Number(e.target.value) || SHOT_MIN_SECONDS)),
              )
            }
          />
        </div>
        <div>
          <L hint="(blank: the project's style)">Style for this shot</L>
          <Input value={data.style} onChange={(e) => set('style', e.target.value)} placeholder={project.style} />
        </div>
        <div className="sm:col-span-2">
          <L hint="lighting, materials, mood">Look</L>
          <Input
            value={data.look}
            onChange={(e) => set('look', e.target.value)}
            placeholder="e.g. glossy materials, warm cinematic lighting"
          />
        </div>
      </div>

      <SectionTitle hint="English. Write [Name] for a character, [Product] for the product, {dialogue} where the lines are spoken (optional).">
        What we see
      </SectionTitle>
      <Textarea
        rows={5}
        value={data.picture}
        onChange={(e) => set('picture', e.target.value)}
        placeholder="The opening picture and what happens…"
      />
      <div className="flex flex-wrap gap-1 pt-1">
        {data.cast.map((m) => {
          const name = m.type === 'product' ? 'Product' : charName(m.id);
          return (
            <Button
              key={`${m.type}${m.id}`}
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => insertToken(`[${name}]`)}
            >
              + [{name}]
            </Button>
          );
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => insertToken('{dialogue}')}
        >
          + {'{dialogue}'}
        </Button>
      </div>
      <div className="pt-2">
        <L>Camera</L>
        <Input
          value={data.camera}
          onChange={(e) => set('camera', e.target.value)}
          placeholder="e.g. The camera pushes in slowly. (add 'No dialogue.' when nobody speaks)"
        />
      </div>

      <SectionTitle hint="The order matters in shots with pictures: the first is <Subject 1>.">
        Who and what is in the shot
      </SectionTitle>
      <div className="flex flex-wrap gap-2">
        {project.product && (
          <button
            type="button"
            onClick={() => toggleCast('product', project.product!.id)}
            className={`rounded-full border px-2.5 py-1 text-sm ${inShot('product', project.product.id) ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent'}`}
          >
            📦 {project.product.title}
          </button>
        )}
        {knownChars.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => toggleCast('character', c.id)}
            className={`rounded-full border px-2.5 py-1 text-sm ${inShot('character', c.id) ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent'} ${castChars.some((x) => x.id === c.id) ? '' : 'opacity-70'}`}
          >
            {c.name}
          </button>
        ))}
      </div>
      {data.cast.length > 0 && (
        <div className="mt-2 space-y-2">
          {data.cast.map((m, i) => (
            <div
              key={`${m.type}${m.id}`}
              className="grid gap-2 rounded-md border p-2 sm:grid-cols-[auto_1fr_150px_auto] sm:items-center"
            >
              <Badge variant="secondary">
                {mode === 'references' ? `Subject ${i + 1}` : ''}{' '}
                {m.type === 'product' ? (project.product?.title ?? 'Product') : charName(m.id)}
              </Badge>
              <Input
                className="h-8"
                value={m.note}
                onChange={(e) => updateMember(i, { note: e.target.value })}
                placeholder="Description in this shot only (blank: the usual one)"
              />
              <NativeSelect
                className="h-8"
                value={m.picture ?? ''}
                onChange={(e) => updateMember(i, { picture: e.target.value ? Number(e.target.value) : null })}
                disabled={!data.pictures.length}
              >
                <option value="">no picture</option>
                {data.pictures.map((_, j) => (
                  <option key={j} value={j + 1}>
                    shown in picture {j + 1}
                  </option>
                ))}
              </NativeSelect>
              <div className="flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => moveMember(i, -1)}
                  aria-label="Move up"
                >
                  <ArrowUp />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => moveMember(i, 1)}
                  aria-label="Move down"
                >
                  <ArrowDown />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SectionTitle hint="Continue straight from another shot, OR give up to 3 reference pictures (characters, the product, stills) — not both.">
        Starting picture / reference pictures
      </SectionTitle>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <L>Start from the last frame of</L>
          <NativeSelect value={startsFrom ?? ''} onChange={(e) => set('startFrom', e.target.value || null)}>
            <option value="">— no, a new picture —</option>
            {index > 0 && <option value="previous">the previous shot</option>}
            {startOptions.map((x) => (
              <option key={x.s.id} value={x.s.id}>
                Shot {x.n} {(x.s.data as ShotData).label ? `— ${(x.s.data as ShotData).label}` : ''}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <L>Add a reference picture</L>
          <NativeSelect
            value={addKind}
            onChange={(e) => void onAddKind(e.target.value)}
            disabled={!!startsFrom || data.pictures.length >= MAX_REFERENCE_PICTURES}
          >
            <option value="">
              {startsFrom ? 'not with a starting frame' : data.pictures.length >= 3 ? 'at most 3 pictures' : 'choose…'}
            </option>
            {project.product?.referenceImage && (
              <option value={`product|${project.product.id}`}>Product: {project.product.title}</option>
            )}
            {knownChars
              .filter((c) => c.images.length)
              .flatMap((c) =>
                c.images.map((_, j) => (
                  <option key={`${c.id}${j}`} value={`character|${c.id}|${j}`}>
                    {c.name} — picture {j + 1}
                  </option>
                )),
              )}
            {otherShots.map((x) => (
              <optgroup key={x.s.id} label={`Shot ${x.n}`}>
                <option value={`shot|${x.s.id}|last`}>Shot {x.n} — last frame</option>
                <option value={`shot|${x.s.id}|mid`}>Shot {x.n} — middle</option>
              </optgroup>
            ))}
            <option value="upload">Upload a picture…</option>
          </NativeSelect>
        </div>
      </div>
      {data.pictures.length > 0 && (
        <div className={`mt-2 flex flex-wrap gap-3 ${startsFrom ? 'opacity-50' : ''}`}>
          {data.pictures.map((p, i) => {
            const url = pictureUrl(p, project, characters);
            return (
              <div key={i} className="w-28 text-xs">
                <div className="relative grid h-36 place-items-center overflow-hidden rounded-md border bg-muted">
                  {url ? (
                    <img src={url} alt="" className="size-full object-cover" />
                  ) : (
                    <ImagePlus className="size-5 text-muted-foreground" />
                  )}
                  <span className="absolute top-1 left-1 rounded bg-black/60 px-1 text-white">Picture {i + 1}</span>
                  <button
                    type="button"
                    className="absolute top-1 right-1 rounded-full bg-black/60 p-0.5 text-white"
                    onClick={() => removePicture(i)}
                    aria-label="Remove picture"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
                <div className="mt-1 line-clamp-2 text-muted-foreground">{pictureLabel(p, project, characters)}</div>
              </div>
            );
          })}
        </div>
      )}
      {mode === 'references' && (
        <div className="mt-3 grid gap-3">
          <div>
            <L hint="(blank: made from the first sentence above)">Summary</L>
            <Textarea rows={2} value={data.summary} onChange={(e) => set('summary', e.target.value)} />
          </div>
          <div>
            <L hint="(blank: keep each subject exactly as in its picture)">What to keep from the pictures</L>
            <Textarea rows={2} value={data.retention} onChange={(e) => set('retention', e.target.value)} />
          </div>
        </div>
      )}

      <SectionTitle hint="Spoken words exactly as they should sound — Hindi in Devanagari. Never in quotes.">
        Dialogue
      </SectionTitle>
      <div className="space-y-2">
        {data.lines.map((l, i) => (
          <div key={l.id} className="space-y-2 rounded-md border p-2">
            <div className="grid gap-2 sm:grid-cols-[180px_1fr_1fr_auto]">
              <NativeSelect
                className="h-8"
                value={l.characterId ?? ''}
                onChange={(e) =>
                  setLine(i, {
                    characterId: e.target.value || null,
                    speakerName: e.target.value ? '' : l.speakerName || 'A narrator',
                  })
                }
              >
                {knownChars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
                <option value="">Other voice…</option>
              </NativeSelect>
              {l.characterId ? (
                <Input
                  className="h-8"
                  value={l.delivery}
                  onChange={(e) => setLine(i, { delivery: e.target.value })}
                  placeholder="says / shouts / says into the phone"
                />
              ) : (
                <Input
                  className="h-8"
                  value={l.speakerName}
                  onChange={(e) => setLine(i, { speakerName: e.target.value })}
                  placeholder="e.g. A narrator"
                />
              )}
              <Input
                className="h-8"
                value={l.voice}
                onChange={(e) => setLine(i, { voice: e.target.value })}
                placeholder={
                  (l.characterId && knownChars.find((c) => c.id === l.characterId)?.voiceDescription) ||
                  'voice, e.g. a warm female voice'
                }
              />
              <div className="flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => moveLine(i, -1)}
                  aria-label="Move line up"
                >
                  <ArrowUp />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={() => moveLine(i, 1)}
                  aria-label="Move line down"
                >
                  <ArrowDown />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive"
                  onClick={() =>
                    set(
                      'lines',
                      data.lines.filter((_, j) => j !== i),
                    )
                  }
                  aria-label="Delete line"
                >
                  <Trash2 />
                </Button>
              </div>
            </div>
            {!l.characterId && (
              <Input
                className="h-8"
                value={l.delivery}
                onChange={(e) => setLine(i, { delivery: e.target.value })}
                placeholder="says / whispers"
              />
            )}
            <Textarea
              lang={(l.language || project.language) === 'Hindi' ? 'hi' : undefined}
              className="hindi min-h-14"
              rows={2}
              value={l.text}
              onChange={(e) => setLine(i, { text: e.target.value })}
              placeholder="ब्रेन बाबू, सब कुछ साफ़-सफ़ाई हो गया है।"
            />
            <div className="flex flex-wrap gap-2">
              <NativeSelect
                className="h-8 w-40"
                value={l.language}
                onChange={(e) => setLine(i, { language: e.target.value })}
              >
                <option value="">{project.language} (project)</option>
                {LANGUAGES.filter((x) => x !== project.language).map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </NativeSelect>
              {data.cuts.length > 0 && (
                <NativeSelect
                  className="h-8 w-48"
                  value={l.cut}
                  onChange={(e) => setLine(i, { cut: Number(e.target.value) })}
                >
                  <option value={0}>in the opening picture</option>
                  {data.cuts.map((c, j) => (
                    <option key={j} value={j + 1}>
                      after cut {j + 1} ({c.at} s)
                    </option>
                  ))}
                </NativeSelect>
              )}
            </div>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addLine}>
          <Plus /> Add a line
        </Button>
      </div>

      <SectionTitle hint="Optional: the camera cuts to a new angle inside the same shot.">Camera cuts</SectionTitle>
      <div className="space-y-2">
        {data.cuts.map((c, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[90px_1fr_auto]">
            <Input
              type="number"
              step={0.1}
              min={0}
              max={data.seconds}
              value={c.at}
              onChange={(e) =>
                set(
                  'cuts',
                  data.cuts.map((x, j) => (j === i ? { ...x, at: Number(e.target.value) } : x)),
                )
              }
              aria-label="Seconds"
            />
            <Input
              value={c.description}
              onChange={(e) =>
                set(
                  'cuts',
                  data.cuts.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)),
                )
              }
              placeholder="a medium close-up of [Name], who …"
            />
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive"
              onClick={() =>
                set(
                  'cuts',
                  data.cuts.filter((_, j) => j !== i),
                )
              }
              aria-label="Delete cut"
            >
              <Trash2 />
            </Button>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => set('cuts', [...data.cuts, { at: Math.round(data.seconds * 5) / 10, description: '' }])}
          disabled={data.cuts.length >= 4}
        >
          <Plus /> Add a cut
        </Button>
      </div>

      <SectionTitle>Sound</SectionTitle>
      <div className="grid gap-3">
        <div>
          <L hint="1–4 sentences, no music">Sounds of the scene</L>
          <Textarea
            rows={2}
            value={data.sound}
            onChange={(e) => set('sound', e.target.value)}
            placeholder="e.g. A low factory hum, brooms swishing…"
          />
        </div>
        <div>
          <L hint="1–3 sentences, blank = none">Music</L>
          <Textarea
            rows={2}
            value={data.music}
            onChange={(e) => set('music', e.target.value)}
            placeholder="e.g. Light, playful pizzicato strings, quiet under the voice."
          />
        </div>
      </div>

      <SectionTitle>Other</SectionTitle>
      <SwitchRow
        label="No on-screen text"
        description='Off only when a screen, sign or label with words is shown ("No subtitles or captions.").'
        checked={data.noOnScreenText}
        onCheckedChange={(v) => set('noOnScreenText', v)}
      />
      <div className="pb-2">
        <L hint="(for the team: transitions, continuity; never sent to FreeVideo)">Notes</L>
        <Textarea rows={2} value={data.notes ?? ''} onChange={(e) => set('notes', e.target.value)} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <L hint="(blank: a new one each take)">Fixed seed</L>
          <Input
            type="number"
            value={data.seed ?? ''}
            onChange={(e) =>
              set('seed', e.target.value === '' ? null : Math.max(0, Math.floor(Number(e.target.value))))
            }
            placeholder="random"
          />
        </div>
      </div>
    </div>
  );
}
